// src/components/GraiaPaymentForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import './GraiaPaymentForm.css';
import { Zap, ShieldCheck, Smartphone } from 'lucide-react';

type PaymentMethod = 'credit' | 'debit' | 'pix' | 'boleto';

interface GraiaPaymentFormProps {
    amount: number; // em centavos
    userEmail?: string;
    onSubmitCard: (cardData: CardFormData) => Promise<void>;
    onSubmitPix: () => Promise<void>;
    onSubmitBoleto: (cpf: string, name: string, address: {
        zip_code: string; street_name: string; street_number: string;
        neighborhood: string; city: string; federal_unit: string;
    }) => Promise<void>;
    processing?: boolean;
    error?: string;
}

export interface CardFormData {
    cardNumber: string;
    cardHolder: string;
    expiry: string;
    cvv: string;
    cpf: string;
    installments: number;
    paymentType: 'credit' | 'debit';
}

function formatCardNumber(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + '/' + digits.slice(2);
    return digits;
}

function formatCPF(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.slice(0, 3) + '.' + digits.slice(3);
    if (digits.length <= 9) return digits.slice(0, 3) + '.' + digits.slice(3, 6) + '.' + digits.slice(6);
    return digits.slice(0, 3) + '.' + digits.slice(3, 6) + '.' + digits.slice(6, 9) + '-' + digits.slice(9);
}

function detectCardBrand(number: string): string {
    const n = number.replace(/\s/g, '');
    if (/^4/.test(n)) return 'visa';
    if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return 'mastercard';
    if (/^3[47]/.test(n)) return 'amex';
    if (/^6(?:011|5)/.test(n)) return 'elo';
    return '';
}

const CardBrandIcon: React.FC<{ brand: string }> = ({ brand }) => {
    const icons: Record<string, React.ReactNode> = {
        visa: (
            <svg viewBox="0 0 38 24" width="38" height="24">
                <rect width="38" height="24" rx="4" fill="#1A1F71" />
                <text x="19" y="17" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial">VISA</text>
            </svg>
        ),
        mastercard: (
            <svg viewBox="0 0 38 24" width="38" height="24">
                <rect width="38" height="24" rx="4" fill="#252525" />
                <circle cx="15" cy="12" r="7" fill="#EB001B" />
                <circle cx="23" cy="12" r="7" fill="#F79E1B" />
                <path d="M19 7.5a7 7 0 0 1 0 9 7 7 0 0 1 0-9z" fill="#FF5F00" />
            </svg>
        ),
        elo: (
            <svg viewBox="0 0 38 24" width="38" height="24">
                <rect width="38" height="24" rx="4" fill="#00A4E0" />
                <text x="19" y="17" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial">ELO</text>
            </svg>
        ),
    };
    return brand && icons[brand] ? <span className="gpf-card-brand">{icons[brand]}</span> : null;
};

const GraiaPaymentForm: React.FC<GraiaPaymentFormProps> = ({
    amount,
    userEmail: _userEmail,
    onSubmitCard,
    onSubmitPix,
    onSubmitBoleto,
    processing = false,
    error,
}) => {
    const [method, setMethod] = useState<PaymentMethod>('credit');


    // Card fields
    const [cardNumber, setCardNumber] = useState('');
    const [cardHolder, setCardHolder] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvv, setCvv] = useState('');
    const [cpf, setCpf] = useState('');
    const [installments, setInstallments] = useState(1);
    const [boletoDoc, setBoletoDoc] = useState('');
    const [boletoName, setBoletoName] = useState('');
    const [boletoZip, setBoletoZip] = useState('');
    const [boletoStreet, setBoletoStreet] = useState('');
    const [boletoNumber, setBoletoNumber] = useState('');
    const [boletoNeighborhood, setBoletoNeighborhood] = useState('');
    const [boletoCity, setBoletoCity] = useState('');
    const [boletoState, setBoletoState] = useState('');
    const [flipped, setFlipped] = useState(false);

    // Validation errors
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Refs for Tab navigation
    const cardNumberRef = useRef<HTMLInputElement>(null);
    const cardHolderRef = useRef<HTMLInputElement>(null);
    const expiryRef = useRef<HTMLInputElement>(null);
    const cvvRef = useRef<HTMLInputElement>(null);
    const cpfRef = useRef<HTMLInputElement>(null);

    const brand = detectCardBrand(cardNumber);

    const [installmentOptions, setInstallmentOptions] = useState<{ installments: number; installmentAmount: number; totalAmount: number }[]>([]);
    const [loadingInstallments, setLoadingInstallments] = useState(false);


    useEffect(() => {
        if (method !== 'credit' || !amount) return;

        const fetchInstallments = async () => {
            setLoadingInstallments(true);
            try {
                const mp = (window as any).__mpInstance;
                if (!mp) return;

                // Detectar bin do cartão (primeiros 6 dígitos)
                const bin = cardNumber.replace(/\s/g, '').slice(0, 6);
                if (bin.length < 6) {
                    setInstallmentOptions([]);
                    return;
                }

                const result = await mp.getInstallments({
                    amount: String(amount / 100),
                    bin,
                    locale: 'pt-BR',
                });

                if (result && result[0]?.payer_costs) {
                    const costs = result[0].payer_costs.map((p: any) => ({
                        installments: p.installments,
                        installmentAmount: Math.round(p.installment_amount * 100),
                        totalAmount: Math.round(p.total_amount * 100),
                    }));
                    setInstallmentOptions(costs);
                }
            } catch (e) {
                console.error('Erro ao buscar parcelas:', e);
            } finally {
                setLoadingInstallments(false);
            }
        };

        fetchInstallments();
    }, [method, amount, cardNumber]);

    const formatPrice = (cents: number) =>
        (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    function validateCard(): boolean {
        const errs: Record<string, string> = {};
        const rawNum = cardNumber.replace(/\s/g, '');
        if (rawNum.length < 13) errs.cardNumber = 'Número inválido';
        if (cardHolder.trim().length < 3) errs.cardHolder = 'Nome obrigatório';
        const [m, y] = expiry.split('/');
        const now = new Date();
        if (!m || !y || parseInt(m) > 12 || parseInt('20' + y) < now.getFullYear() ||
            (parseInt('20' + y) === now.getFullYear() && parseInt(m) < now.getMonth() + 1))
            errs.expiry = 'Data inválida';
        if (cvv.length < 3) errs.cvv = 'CVV inválido';
        const rawCpf = cpf.replace(/\D/g, '');
        if (rawCpf.length !== 11) errs.cpf = 'CPF inválido';
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    }

    function validateBoleto(): boolean {
        const errs: Record<string, string> = {};
        if (boletoName.trim().split(' ').length < 2) errs.boletoName = 'Informe nome e sobrenome';
        if (boletoDoc.replace(/\D/g, '').length !== 11) errs.boletoDoc = 'CPF inválido';
        if (boletoZip.replace(/\D/g, '').length !== 8) errs.boletoZip = 'CEP inválido';
        if (!boletoStreet.trim()) errs.boletoStreet = 'Rua obrigatória';
        if (!boletoNumber.trim()) errs.boletoNumber = 'Número obrigatório';
        if (!boletoNeighborhood.trim()) errs.boletoNeighborhood = 'Bairro obrigatório';
        if (!boletoCity.trim()) errs.boletoCity = 'Cidade obrigatória';
        if (boletoState.trim().length !== 2) errs.boletoState = 'UF inválida';
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    }

    async function handleCardSubmit(e: React.MouseEvent) {
        e.preventDefault();
        if (!validateCard()) return;
        await onSubmitCard({
            cardNumber: cardNumber.replace(/\s/g, ''),
            cardHolder: cardHolder.trim().toUpperCase(),
            expiry,
            cvv,
            cpf: cpf.replace(/\D/g, ''),
            installments,
            paymentType: method as 'credit' | 'debit',
        });
    }

    async function fetchCEP(cep: string) {
        const digits = cep.replace(/\D/g, '');
        if (digits.length !== 8) return;
        try {
            const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await res.json();
            if (!data.erro) {
                setBoletoStreet(data.logradouro?.toUpperCase() || '');
                setBoletoNeighborhood(data.bairro?.toUpperCase() || '');
                setBoletoCity(data.localidade?.toUpperCase() || '');
                setBoletoState(data.uf?.toUpperCase() || '');
            }
        } catch { }
    }

    async function handleBoletoSubmit(e: React.MouseEvent) {
        e.preventDefault();
        if (!validateBoleto()) return;
        await onSubmitBoleto(boletoDoc.replace(/\D/g, ''), boletoName.trim(), {
            zip_code: boletoZip.replace(/\D/g, ''),
            street_name: boletoStreet.trim(),
            street_number: boletoNumber.trim(),
            neighborhood: boletoNeighborhood.trim(),
            city: boletoCity.trim(),
            federal_unit: boletoState.trim().toUpperCase(),
        });
    }

    const methods: { id: PaymentMethod; label: string; icon: React.ReactNode }[] = [
        {
            id: 'credit',
            label: 'Crédito',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
            ),
        },
        {
            id: 'pix',
            label: 'Pix',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.354 3.646a.9.9 0 0 1 1.272 0l2.496 2.497a3.6 3.6 0 0 0 5.087 0 .9.9 0 0 1 1.273 1.273 5.4 5.4 0 0 1-7.633 0L12 6.067l-1.85 1.849a5.4 5.4 0 0 1-7.632 0A.9.9 0 1 1 3.79 6.643a3.6 3.6 0 0 0 5.087 0l2.497-2.497ZM2.52 10.79a.9.9 0 0 1 1.27 0 3.6 3.6 0 0 0 5.087 0l1.85-1.85a.9.9 0 0 1 1.273 0l1.849 1.85a3.6 3.6 0 0 0 5.087 0 .9.9 0 1 1 1.272 1.271 5.4 5.4 0 0 1-7.633 0L12 10.212l-1.214 1.214v.001a5.4 5.4 0 0 1-7.633-.364.9.9 0 0 1 .368-1.273Zm.908 4.547a5.4 5.4 0 0 1 7.337.547l1.228 1.228 1.228-1.228a5.4 5.4 0 0 1 7.337-.547.9.9 0 1 1-1.097 1.43 3.6 3.6 0 0 0-4.968.364l-1.864 1.864a.9.9 0 0 1-1.272 0l-1.864-1.864a3.6 3.6 0 0 0-4.968-.364.9.9 0 1 1-1.097-1.43Z" />
                </svg>
            ),
        },
        {
            id: 'boleto',
            label: 'Boleto',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h2v16H4zm3 0h1v16H7zm3 0h2v16h-2zm4 0h1v16h-1zm3 0h2v16h-2z" />
                </svg>
            ),
        },
    ];

    return (
        <div className="gpf-root">
            {/* Method Tabs */}
            <div className="gpf-methods" role="tablist">
                {methods.map((m) => (
                    <button
                        key={m.id}
                        role="tab"
                        aria-selected={method === m.id}
                        className={`gpf-method-btn ${method === m.id ? 'active' : ''}`}
                        onClick={() => { setMethod(m.id); setFieldErrors({}); }}
                        type="button"
                    >
                        <span className="gpf-method-icon">{m.icon}</span>
                        <span className="gpf-method-label">{m.label}</span>
                    </button>
                ))}
            </div>

            {/* ── CARTÃO (crédito / débito) ── */}
            {(method === 'credit' || method === 'debit') && (
                <div className="gpf-panel" role="tabpanel">
                    {/* Card Preview */}
                    <div className={`gpf-card-preview ${flipped ? 'flipped' : ''}`} aria-hidden="true">
                        <div className="gpf-card-front">
                            <div className="gpf-card-chip">
                                <svg width="30" height="24" viewBox="0 0 30 24">
                                    <rect width="30" height="24" rx="4" fill="#d4af37" opacity=".9" />
                                    <rect x="11" y="0" width="8" height="24" fill="#c9a227" opacity=".5" />
                                    <rect x="0" y="8" width="30" height="8" fill="#c9a227" opacity=".5" />
                                    <rect x="11" y="8" width="8" height="8" fill="#b8941c" opacity=".7" />
                                </svg>
                            </div>
                            <div className="gpf-card-number-display">
                                {(cardNumber || '•••• •••• •••• ••••').padEnd(19, ' ').split('').map((c, i) => (
                                    <span key={i} className={c === ' ' ? 'gpf-card-space' : 'gpf-card-digit'}>{c || '•'}</span>
                                ))}
                            </div>
                            <div className="gpf-card-bottom">
                                <div>
                                    <div className="gpf-card-label">Titular</div>
                                    <div className="gpf-card-value">{cardHolder || 'NOME DO TITULAR'}</div>
                                </div>
                                <div>
                                    <div className="gpf-card-label">Validade</div>
                                    <div className="gpf-card-value">{expiry || 'MM/AA'}</div>
                                </div>
                                {brand && <CardBrandIcon brand={brand} />}
                            </div>
                            <div className="gpf-card-type-badge">{method === 'credit' ? 'CRÉDITO' : 'DÉBITO'}</div>
                        </div>
                        <div className="gpf-card-back">
                            <div className="gpf-card-stripe" />
                            <div className="gpf-card-cvv-area">
                                <div className="gpf-card-cvv-label">CVV</div>
                                <div className="gpf-card-cvv-box">{cvv || '•••'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="gpf-fields">
                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-card-number">Número do cartão</label>
                            <div className="gpf-input-wrapper">
                                <input
                                    id="gpf-card-number"
                                    ref={cardNumberRef}
                                    className={`gpf-input gpf-input-card ${fieldErrors.cardNumber ? 'error' : ''}`}
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="cc-number"
                                    placeholder="0000 0000 0000 0000"
                                    value={cardNumber}
                                    maxLength={19}
                                    onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                                    onKeyDown={e => {
                                        if (e.key === 'Tab' && !e.shiftKey && cardNumber.replace(/\s/g, '').length === 16) {
                                            e.preventDefault();
                                            cardHolderRef.current?.focus();
                                        }
                                    }}
                                />
                                {brand && <CardBrandIcon brand={brand} />}
                            </div>
                            {fieldErrors.cardNumber && <span className="gpf-field-error">{fieldErrors.cardNumber}</span>}
                        </div>

                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-card-holder">Nome no cartão</label>
                            <input
                                id="gpf-card-holder"
                                ref={cardHolderRef}
                                className={`gpf-input ${fieldErrors.cardHolder ? 'error' : ''}`}
                                type="text"
                                autoComplete="cc-name"
                                placeholder="Como está impresso no cartão"
                                value={cardHolder}
                                onChange={e => setCardHolder(e.target.value.toUpperCase())}
                                onKeyDown={e => {
                                    if (e.key === 'Tab' && !e.shiftKey) {
                                        e.preventDefault();
                                        expiryRef.current?.focus();
                                    }
                                }}
                            />
                            {fieldErrors.cardHolder && <span className="gpf-field-error">{fieldErrors.cardHolder}</span>}
                        </div>

                        <div className="gpf-fields-row">
                            <div className="gpf-field-group">
                                <label className="gpf-label" htmlFor="gpf-expiry">Validade</label>
                                <input
                                    id="gpf-expiry"
                                    ref={expiryRef}
                                    className={`gpf-input ${fieldErrors.expiry ? 'error' : ''}`}
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="cc-exp"
                                    placeholder="MM/AA"
                                    value={expiry}
                                    maxLength={5}
                                    onChange={e => setExpiry(formatExpiry(e.target.value))}
                                    onKeyDown={e => {
                                        if (e.key === 'Tab' && !e.shiftKey) {
                                            e.preventDefault();
                                            cvvRef.current?.focus();
                                        }
                                    }}
                                />
                                {fieldErrors.expiry && <span className="gpf-field-error">{fieldErrors.expiry}</span>}
                            </div>

                            <div className="gpf-field-group">
                                <label className="gpf-label" htmlFor="gpf-cvv">CVV</label>
                                <input
                                    id="gpf-cvv"
                                    ref={cvvRef}
                                    className={`gpf-input ${fieldErrors.cvv ? 'error' : ''}`}
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="cc-csc"
                                    placeholder="•••"
                                    value={cvv}
                                    maxLength={4}
                                    onChange={e => setCvv(e.target.value.replace(/\D/g, ''))}
                                    onFocus={() => setFlipped(true)}
                                    onBlur={() => setFlipped(false)}
                                    onKeyDown={e => {
                                        if (e.key === 'Tab' && !e.shiftKey) {
                                            e.preventDefault();
                                            cpfRef.current?.focus();
                                        }
                                    }}
                                />
                                {fieldErrors.cvv && <span className="gpf-field-error">{fieldErrors.cvv}</span>}
                            </div>
                        </div>

                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-cpf">CPF do titular</label>
                            <input
                                id="gpf-cpf"
                                ref={cpfRef}
                                className={`gpf-input ${fieldErrors.cpf ? 'error' : ''}`}
                                type="tel"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="000.000.000-00"
                                value={cpf}
                                maxLength={14}
                                onChange={e => setCpf(formatCPF(e.target.value))}
                            />
                            {fieldErrors.cpf && <span className="gpf-field-error">{fieldErrors.cpf}</span>}
                        </div>

                        {method === 'credit' && (
                            <div className="gpf-field-group">
                                <label className="gpf-label" htmlFor="gpf-installments">
                                    Parcelas
                                    {loadingInstallments && (
                                        <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>— carregando...</span>
                                    )}
                                </label>

                                {cardNumber.replace(/\s/g, '').length < 6 ? (
                                    <div className="gpf-installments-placeholder">
                                        Digite o número do cartão para ver as opções de parcelamento
                                    </div>
                                ) : (
                                    <select
                                        id="gpf-installments"
                                        className="gpf-select"
                                        value={installments}
                                        onChange={e => setInstallments(Number(e.target.value))}
                                        disabled={loadingInstallments || installmentOptions.length === 0}
                                    >
                                        {installmentOptions.map(opt => (
                                            <option key={opt.installments} value={opt.installments}>
                                                {opt.installments}x de {formatPrice(opt.installmentAmount)}
                                                {opt.totalAmount > amount
                                                    ? ` (total ${formatPrice(opt.totalAmount)})`
                                                    : ' (sem juros)'}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        <button
                            className="gpf-submit-btn"
                            onClick={handleCardSubmit}
                            disabled={processing}
                            type="button"
                        >
                            {processing ? (
                                <span className="gpf-btn-loading">
                                    <span className="gpf-spinner" />
                                    Processando...
                                </span>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    </svg>
                                    Pagar {formatPrice(amount)}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* ── PIX ── */}
            {method === 'pix' && (
                <div className="gpf-panel gpf-pix-panel" role="tabpanel">
                    <div className="gpf-pix-illustration">
                        <div className="gpf-pix-icon-wrap">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11.354 3.646a.9.9 0 0 1 1.272 0l2.496 2.497a3.6 3.6 0 0 0 5.087 0 .9.9 0 0 1 1.273 1.273 5.4 5.4 0 0 1-7.633 0L12 6.067l-1.85 1.849a5.4 5.4 0 0 1-7.632 0A.9.9 0 1 1 3.79 6.643a3.6 3.6 0 0 0 5.087 0l2.497-2.497ZM2.52 10.79a.9.9 0 0 1 1.27 0 3.6 3.6 0 0 0 5.087 0l1.85-1.85a.9.9 0 0 1 1.273 0l1.849 1.85a3.6 3.6 0 0 0 5.087 0 .9.9 0 1 1 1.272 1.271 5.4 5.4 0 0 1-7.633 0L12 10.212l-1.214 1.214v.001a5.4 5.4 0 0 1-7.633-.364.9.9 0 0 1 .368-1.273Zm.908 4.547a5.4 5.4 0 0 1 7.337.547l1.228 1.228 1.228-1.228a5.4 5.4 0 0 1 7.337-.547.9.9 0 1 1-1.097 1.43 3.6 3.6 0 0 0-4.968.364l-1.864 1.864a.9.9 0 0 1-1.272 0l-1.864-1.864a3.6 3.6 0 0 0-4.968-.364.9.9 0 1 1-1.097-1.43Z" />
                            </svg>
                        </div>
                        <h3 className="gpf-pix-title">Pagar com Pix</h3>
                        <p className="gpf-pix-subtitle">O QR Code será gerado após confirmar. Aprovação em segundos.</p>
                    </div>

                    <div className="gpf-pix-info-row">
                        <div className="gpf-pix-info-item">
                            <Zap size={20} strokeWidth={2} />
                            <span>Aprovação imediata</span>
                        </div>
                        <div className="gpf-pix-info-item">
                            <ShieldCheck size={20} strokeWidth={2} />
                            <span>100% seguro</span>
                        </div>
                        <div className="gpf-pix-info-item">
                            <Smartphone size={20} strokeWidth={2} />
                            <span>Qualquer banco</span>
                        </div>
                    </div>

                    <div className="gpf-pix-amount">
                        <span className="gpf-pix-amount-label">Valor a pagar</span>
                        <span className="gpf-pix-amount-value">{formatPrice(amount)}</span>
                    </div>

                    <button
                        className="gpf-submit-btn gpf-submit-pix"
                        onClick={async (e) => { e.preventDefault(); await onSubmitPix(); }}
                        disabled={processing}
                        type="button"
                    >
                        {processing ? (
                            <span className="gpf-btn-loading">
                                <span className="gpf-spinner" />
                                Gerando QR Code...
                            </span>
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M11.354 3.646a.9.9 0 0 1 1.272 0l2.496 2.497a3.6 3.6 0 0 0 5.087 0 .9.9 0 0 1 1.273 1.273 5.4 5.4 0 0 1-7.633 0L12 6.067l-1.85 1.849a5.4 5.4 0 0 1-7.632 0A.9.9 0 1 1 3.79 6.643a3.6 3.6 0 0 0 5.087 0l2.497-2.497ZM2.52 10.79a.9.9 0 0 1 1.27 0 3.6 3.6 0 0 0 5.087 0l1.85-1.85a.9.9 0 0 1 1.273 0l1.849 1.85a3.6 3.6 0 0 0 5.087 0 .9.9 0 1 1 1.272 1.271 5.4 5.4 0 0 1-7.633 0L12 10.212l-1.214 1.214v.001a5.4 5.4 0 0 1-7.633-.364.9.9 0 0 1 .368-1.273Zm.908 4.547a5.4 5.4 0 0 1 7.337.547l1.228 1.228 1.228-1.228a5.4 5.4 0 0 1 7.337-.547.9.9 0 1 1-1.097 1.43 3.6 3.6 0 0 0-4.968.364l-1.864 1.864a.9.9 0 0 1-1.272 0l-1.864-1.864a3.6 3.6 0 0 0-4.968-.364.9.9 0 1 1-1.097-1.43Z" />
                                </svg>
                                Gerar QR Code — {formatPrice(amount)}
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* ── BOLETO ── */}
            {method === 'boleto' && (
                <div className="gpf-panel gpf-boleto-panel" role="tabpanel">
                    <div className="gpf-boleto-illustration">
                        <div className="gpf-boleto-icon-wrap">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M4 4h2v16H4zm3 0h1v16H7zm3 0h2v16h-2zm4 0h1v16h-1zm3 0h2v16h-2z" />
                            </svg>
                        </div>
                        <h3 className="gpf-boleto-title">Pagar com Boleto</h3>
                        <p className="gpf-boleto-subtitle">Vencimento em 1 dia útil. O acesso será liberado após a compensação bancária.</p>
                    </div>

                    <div className="gpf-boleto-warning">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                        </svg>
                        O acesso é liberado em até 3 dias úteis após o pagamento.
                    </div>

                    <div className="gpf-fields">

                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-boleto-name">Nome completo</label>
                            <input
                                id="gpf-boleto-name"
                                className={`gpf-input ${fieldErrors.boletoName ? 'error' : ''}`}
                                type="text"
                                autoComplete="name"
                                placeholder="Nome e sobrenome"
                                value={boletoName}
                                onChange={e => setBoletoName(e.target.value.toUpperCase())}
                            />
                            {fieldErrors.boletoName && <span className="gpf-field-error">{fieldErrors.boletoName}</span>}
                        </div>

                        {/* CEP */}
                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-boleto-zip">CEP</label>
                            <input
                                id="gpf-boleto-zip"
                                className={`gpf-input ${fieldErrors.boletoZip ? 'error' : ''}`}
                                type="tel" inputMode="numeric" placeholder="00000-000"
                                value={boletoZip} maxLength={9}
                                onChange={e => {
                                    const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                                    const fmt = v.length > 5 ? v.slice(0, 5) + '-' + v.slice(5) : v;
                                    setBoletoZip(fmt);
                                    if (v.length === 8) fetchCEP(v);
                                }}
                            />
                            {fieldErrors.boletoZip && <span className="gpf-field-error">{fieldErrors.boletoZip}</span>}
                        </div>

                        {/* Rua + Número */}
                        <div className="gpf-fields-row">
                            <div className="gpf-field-group">
                                <label className="gpf-label" htmlFor="gpf-boleto-street">Rua</label>
                                <input id="gpf-boleto-street" className={`gpf-input ${fieldErrors.boletoStreet ? 'error' : ''}`}
                                    type="text" placeholder="Nome da rua" value={boletoStreet}
                                    onChange={e => setBoletoStreet(e.target.value.toUpperCase())} />
                                {fieldErrors.boletoStreet && <span className="gpf-field-error">{fieldErrors.boletoStreet}</span>}
                            </div>
                            <div className="gpf-field-group" style={{ maxWidth: 100 }}>
                                <label className="gpf-label" htmlFor="gpf-boleto-number">Nº</label>
                                <input id="gpf-boleto-number" className={`gpf-input ${fieldErrors.boletoNumber ? 'error' : ''}`}
                                    type="text" placeholder="123" value={boletoNumber}
                                    onChange={e => setBoletoNumber(e.target.value)} />
                                {fieldErrors.boletoNumber && <span className="gpf-field-error">{fieldErrors.boletoNumber}</span>}
                            </div>
                        </div>

                        {/* Bairro */}
                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-boleto-neighborhood">Bairro</label>
                            <input id="gpf-boleto-neighborhood" className={`gpf-input ${fieldErrors.boletoNeighborhood ? 'error' : ''}`}
                                type="text" placeholder="Seu bairro" value={boletoNeighborhood}
                                onChange={e => setBoletoNeighborhood(e.target.value.toUpperCase())} />
                            {fieldErrors.boletoNeighborhood && <span className="gpf-field-error">{fieldErrors.boletoNeighborhood}</span>}
                        </div>

                        {/* Cidade + UF */}
                        <div className="gpf-fields-row">
                            <div className="gpf-field-group">
                                <label className="gpf-label" htmlFor="gpf-boleto-city">Cidade</label>
                                <input id="gpf-boleto-city" className={`gpf-input ${fieldErrors.boletoCity ? 'error' : ''}`}
                                    type="text" placeholder="Sua cidade" value={boletoCity}
                                    onChange={e => setBoletoCity(e.target.value.toUpperCase())} />
                                {fieldErrors.boletoCity && <span className="gpf-field-error">{fieldErrors.boletoCity}</span>}
                            </div>
                            <div className="gpf-field-group" style={{ maxWidth: 80 }}>
                                <label className="gpf-label" htmlFor="gpf-boleto-state">UF</label>
                                <input id="gpf-boleto-state" className={`gpf-input ${fieldErrors.boletoState ? 'error' : ''}`}
                                    type="text" placeholder="SP" value={boletoState} maxLength={2}
                                    onChange={e => setBoletoState(e.target.value.toUpperCase())} />
                                {fieldErrors.boletoState && <span className="gpf-field-error">{fieldErrors.boletoState}</span>}
                            </div>
                        </div>

                        <div className="gpf-field-group">
                            <label className="gpf-label" htmlFor="gpf-boleto-cpf">CPF do pagador</label>
                            <input
                                id="gpf-boleto-cpf"
                                className={`gpf-input ${fieldErrors.boletoDoc ? 'error' : ''}`}
                                type="tel"
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="000.000.000-00"
                                value={boletoDoc}
                                maxLength={14}
                                onChange={e => setBoletoDoc(formatCPF(e.target.value))}
                            />
                            {fieldErrors.boletoDoc && <span className="gpf-field-error">{fieldErrors.boletoDoc}</span>}
                        </div>

                        <button
                            className="gpf-submit-btn gpf-submit-boleto"
                            onClick={handleBoletoSubmit}
                            disabled={processing}
                            type="button"
                        >
                            {processing ? (
                                <span className="gpf-btn-loading">
                                    <span className="gpf-spinner" />
                                    Gerando boleto...
                                </span>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M4 4h2v16H4zm3 0h1v16H7zm3 0h2v16h-2zm4 0h1v16h-1zm3 0h2v16h-2z" />
                                    </svg>
                                    Gerar Boleto — {formatPrice(amount)}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div className="gpf-error-bar">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    {error}
                </div>
            )}

            <div className="gpf-security-footer">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" opacity=".5">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                </svg>
                <span>Pagamento 100% seguro via Mercado Pago</span>
            </div>
        </div>
    );
};

export default GraiaPaymentForm;